// The model and effort picker beside Send (operator 2026-10-05; design brief
// docs/design/poneva-chat/MODEL-PICKER-BRIEF-2026-10-05.md). A quiet chip shows
// what the next answer uses; one tap opens the company's allowed models and a
// four-step effort control. A popover on desktop, the kit bottom sheet on
// phones. Hidden when the server has no model policy (today's behaviour).
import {
  useEffect,
  useId,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { Popover } from 'radix-ui';
import { useAuiState } from '@assistant-ui/react';
import { Check, ChevronDown, Info } from 'lucide-react';
import { useCompany } from '@/company/context';
import { useAppLocale } from '@/i18n/context';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import modelPickerCopy, { type ModelPickerCopyKey } from '@/i18n/ns/model-picker';
import { BottomSheet } from '@/components/ui/sheet';
import { formatters } from '@/usage/format';
import { useNarrowViewport } from '@/lib/mobile';
import { useOptionalWorkspace } from '../context';
import {
  EFFORTS,
  pickerModels,
  type CostHint,
  type Effort,
  type PickerModel,
  type Selection,
} from './model-policy';
import { chooseModelSelection, loadModelPolicy, loadModelThread, useModelPicker } from './store';

type Copy = Readonly<Record<ModelPickerCopyKey, string>>;

const BODY_KEYS: Record<string, ModelPickerCopyKey> = {
  auto: 'autoBody',
  opus: 'opusBody',
  sonnet: 'sonnetBody',
  haiku: 'haikuBody',
  sol: 'solBody',
};

export const modelBody = (copy: Copy, choice: string) => copy[BODY_KEYS[choice] ?? 'otherBody'];
export const effortLabel = (copy: Copy, effort: Effort) => copy[effort];
const effortBody = (copy: Copy, effort: Effort) =>
  copy[`${effort}Body` as `${Effort}Body` & ModelPickerCopyKey];
export const costLabel = (copy: Copy, cost: CostHint) =>
  cost === 'lower' ? copy.costLower : cost === 'higher' ? copy.costHigher : null;

/** A per-run limit in credits ("200 credits"), the way Settings › Usage writes credits. */
export function formatCredits(copy: Copy, credits: number, locale: string) {
  return formatCopy(copy.capCredits, { credits: formatters(locale).int(credits) });
}

/** Four rising bars: how much thought an answer gets. Decorative. */
export function EffortGlyph({ effort, size = 14 }: { effort: Effort; size?: number }) {
  const level = EFFORTS.indexOf(effort);
  return (
    <svg
      className="pc-effort-glyph"
      width={size}
      height={size}
      viewBox="0 0 14 14"
      aria-hidden="true"
      data-level={level + 1}
    >
      {[4, 7, 10, 13].map((height, index) => (
        <rect
          key={height}
          x={0.5 + index * 3.5}
          y={13.5 - height}
          width={2.5}
          height={height}
          rx={1}
          data-on={index <= level || undefined}
        />
      ))}
    </svg>
  );
}

export function ComposerModelPicker({ disabled = false }: { disabled?: boolean }) {
  const ws = useOptionalWorkspace();
  const company = useCompany();
  const organizationId = company.data?.company?.id ?? null;
  const threadId = ws?.threadId ?? null;
  const threadSaved = ws?.threadSaved === true;
  const { policy, selection, wantedEffort } = useModelPicker(organizationId, threadId, threadSaved);
  // After each answer, show what the chat now uses: the server may have changed
  // it (a Skill run, or "switch this chat to …").
  const running = useAuiState((state) => state.thread.isRunning);
  const wasRunning = useRef(running);
  useEffect(() => {
    if (wasRunning.current && !running && threadId && threadSaved)
      loadModelThread(threadId, { refresh: true });
    wasRunning.current = running;
  }, [running, threadId, threadSaved]);
  if (!policy?.state || !selection || !wantedEffort || !threadId || !organizationId) return null;
  const models = pickerModels(policy.state);
  if (!models.length) return null;
  const admin = ['owner', 'admin'].includes(company.data?.membership?.role ?? '');
  return (
    <ModelPickerControl
      models={models}
      allowedEfforts={policy.state.policy.allowedEfforts}
      selection={selection}
      wantedEffort={wantedEffort}
      runCapCredits={policy.state.policy.runCapCredits}
      admin={admin}
      disabled={disabled}
      onOpen={() => loadModelPolicy(organizationId, true)}
      onChoose={(next) => chooseModelSelection(threadId, next)}
    />
  );
}

export function ModelPickerControl({
  models,
  allowedEfforts,
  selection,
  wantedEffort = selection.effort,
  runCapCredits,
  admin,
  disabled = false,
  onOpen,
  onChoose,
}: {
  models: PickerModel[];
  allowedEfforts: Effort[];
  /** What the next answer uses. */
  selection: Selection;
  /** The effort the person asked for; kept while they try a model without it. */
  wantedEffort?: Effort;
  runCapCredits: number;
  admin: boolean;
  disabled?: boolean;
  onOpen?: () => void;
  onChoose: (next: Selection) => void;
}) {
  const copy = useNamespaceCopy(modelPickerCopy);
  const narrow = useNarrowViewport();
  const [open, setOpenState] = useState(false);
  const setOpen = (next: boolean) => {
    if (next && !open) onOpen?.();
    setOpenState(next);
  };
  const trigger = useRef<HTMLButtonElement>(null);
  const model = models.find((item) => item.choice === selection.choice) ?? models[0];
  const label = formatCopy(copy.triggerLabel, {
    model: model.name,
    effort: effortLabel(copy, selection.effort),
  });
  const chooseModel = (choice: string, close: boolean) => {
    if (!models.some((item) => item.choice === choice)) return;
    onChoose({ choice, effort: wantedEffort });
    if (close) setOpen(false);
  };
  const panel = (
    <PickerPanel
      copy={copy}
      models={models}
      allowedEfforts={allowedEfforts}
      selection={selection}
      runCapCredits={runCapCredits}
      admin={admin}
      onModel={chooseModel}
      onEffort={(effort) => onChoose({ choice: model.choice, effort })}
      onNavigate={() => setOpen(false)}
    />
  );
  const chip = (
    <button
      ref={trigger}
      type="button"
      className="pc-model-chip"
      data-slot="composer-model-picker"
      aria-label={label}
      aria-haspopup="dialog"
      aria-expanded={open}
      data-state={open ? 'open' : 'closed'}
      disabled={disabled}
      onClick={narrow ? () => setOpen(true) : undefined}
    >
      <EffortGlyph effort={selection.effort} />
      <span className="pc-model-chip-text">
        <span className="pc-model-chip-model">{model.name}</span>
        <span className="pc-model-chip-effort">{effortLabel(copy, selection.effort)}</span>
      </span>
      <ChevronDown className="pc-model-chip-caret" size={14} strokeWidth={2} aria-hidden="true" />
    </button>
  );
  if (narrow)
    return (
      <>
        {chip}
        <BottomSheet
          open={open}
          onOpenChange={setOpen}
          title={copy.title}
          returnFocus={trigger}
          testId="model-picker-sheet"
          contentProps={{ className: 'pc-model-sheet pc-portal' }}
        >
          {panel}
        </BottomSheet>
      </>
    );
  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>{chip}</Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          className="pc-model-popover pc-portal"
          side="top"
          align="end"
          sideOffset={10}
          collisionPadding={12}
          aria-label={copy.title}
          data-testid="model-picker-popover"
          onOpenAutoFocus={(event) => {
            // Focus the chosen model, so arrows and Enter work at once.
            event.preventDefault();
            (event.currentTarget as HTMLElement | null)
              ?.querySelector<HTMLElement>('[role="radio"][aria-checked="true"]')
              ?.focus();
          }}
        >
          {panel}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

function PickerPanel({
  copy,
  models,
  allowedEfforts,
  selection,
  runCapCredits,
  admin,
  onModel,
  onEffort,
  onNavigate,
}: {
  copy: Copy;
  models: PickerModel[];
  allowedEfforts: Effort[];
  selection: Selection;
  runCapCredits: number;
  admin: boolean;
  onModel: (choice: string, close: boolean) => void;
  onEffort: (effort: Effort) => void;
  onNavigate: () => void;
}) {
  const locale = useAppLocale();
  const modelHeading = useId();
  const model = models.find((item) => item.choice === selection.choice) ?? models[0];
  const shown = EFFORTS.filter(
    (effort) =>
      allowedEfforts.includes(effort) && models.some((item) => item.efforts.includes(effort)),
  );
  return (
    <div className="pc-model-panel">
      <p id={modelHeading} className="pc-model-heading">
        {copy.modelHeading}
      </p>
      <RadioRows
        labelledBy={modelHeading}
        className="pc-model-list"
        value={selection.choice}
        options={models.map((item) => ({ value: item.choice }))}
        onMove={(choice) => onModel(choice, false)}
        onPick={(choice) => onModel(choice, true)}
        render={(value, checked) => {
          const item = models.find((candidate) => candidate.choice === value)!;
          const cost = costLabel(copy, item.cost);
          return (
            <>
              <span className="pc-model-row-copy">
                <span className="pc-model-row-name">
                  {item.name}
                  {cost && (
                    <span className="pc-model-cost" data-cost={item.cost}>
                      {cost}
                    </span>
                  )}
                </span>
                <span className="pc-model-row-body">{modelBody(copy, item.choice)}</span>
              </span>
              <Check
                className="pc-model-check"
                size={16}
                strokeWidth={2.25}
                aria-hidden="true"
                data-on={checked || undefined}
              />
            </>
          );
        }}
      />
      {shown.length > 1 && (
        <EffortSteps
          copy={copy}
          shown={shown}
          model={model}
          value={selection.effort}
          onChange={onEffort}
        />
      )}
      {selection.effort === 'max' && (
        <p className="pc-model-note" role="note">
          <Info size={14} strokeWidth={2} aria-hidden="true" />
          <span>
            {formatCopy(copy.maxNote, { cap: formatCredits(copy, runCapCredits, locale) })}
          </span>
        </p>
      )}
      <p className="pc-model-foot">
        {admin ? (
          <a href="#/settings/company/models" onClick={onNavigate}>
            {copy.manage}
          </a>
        ) : (
          copy.companyNote
        )}
      </p>
    </div>
  );
}

function EffortSteps({
  copy,
  shown,
  model,
  value,
  onChange,
}: {
  copy: Copy;
  shown: Effort[];
  model: PickerModel;
  value: Effort;
  onChange: (effort: Effort) => void;
}) {
  const heading = useId();
  const [preview, setPreview] = useState<Effort | null>(null);
  const described = preview ?? value;
  const unavailable = formatCopy(copy.unsupported, { model: model.name });
  return (
    <div className="pc-effort">
      <p id={heading} className="pc-model-heading">
        {copy.effortHeading}
      </p>
      <RadioRows
        labelledBy={heading}
        className="pc-effort-steps"
        style={
          {
            '--pc-effort-count': shown.length,
            '--pc-effort-at': Math.max(0, shown.indexOf(value)),
          } as CSSProperties
        }
        value={value}
        options={shown.map((effort) => ({
          value: effort,
          disabled: !model.efforts.includes(effort),
        }))}
        onMove={(effort) => onChange(effort as Effort)}
        onPick={(effort) => onChange(effort as Effort)}
        onPreview={(effort) => setPreview(effort as Effort | null)}
        title={(_, disabled) => (disabled ? unavailable : undefined)}
        render={(effort) => (
          <>
            <EffortGlyph effort={effort as Effort} size={16} />
            <span>{effortLabel(copy, effort as Effort)}</span>
            <span className="ws-sr-only">
              {model.efforts.includes(effort as Effort)
                ? effortBody(copy, effort as Effort)
                : unavailable}
            </span>
          </>
        )}
      />
      {/* The meaning, for the eye; each step carries its own for assistive technology. */}
      <p className="pc-effort-meaning" aria-hidden="true">
        {model.efforts.includes(described) ? effortBody(copy, described) : unavailable}
      </p>
    </div>
  );
}

/** One choice of a few: a radio group with one tab stop; arrows move and choose. */
function RadioRows({
  labelledBy,
  className,
  style,
  value,
  options,
  onMove,
  onPick,
  onPreview,
  title,
  render,
}: {
  labelledBy: string;
  className: string;
  style?: CSSProperties;
  value: string;
  options: { value: string; disabled?: boolean }[];
  onMove: (value: string) => void;
  onPick: (value: string) => void;
  onPreview?: (value: string | null) => void;
  title?: (value: string, disabled: boolean) => string | undefined;
  render: (value: string, checked: boolean) => ReactNode;
}) {
  const group = useRef<HTMLDivElement>(null);
  const enabled = options.filter((option) => !option.disabled).map((option) => option.value);
  const stop = enabled.includes(value) ? value : enabled[0];
  const move = (event: KeyboardEvent<HTMLButtonElement>) => {
    const at = enabled.indexOf(event.currentTarget.dataset.value ?? '');
    const last = enabled.length - 1;
    const next = {
      ArrowDown: at === last ? 0 : at + 1,
      ArrowRight: at === last ? 0 : at + 1,
      ArrowUp: at <= 0 ? last : at - 1,
      ArrowLeft: at <= 0 ? last : at - 1,
      Home: 0,
      End: last,
    }[event.key];
    if (next === undefined) return;
    event.preventDefault();
    onMove(enabled[next]);
    group.current
      ?.querySelector<HTMLButtonElement>(`[data-value="${CSS.escape(enabled[next])}"]`)
      ?.focus();
  };
  return (
    <div
      ref={group}
      role="radiogroup"
      aria-labelledby={labelledBy}
      className={className}
      style={style}
    >
      {options.map((option) => {
        const checked = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={checked}
            aria-disabled={option.disabled || undefined}
            tabIndex={option.value === stop ? 0 : -1}
            data-value={option.value}
            title={title?.(option.value, Boolean(option.disabled))}
            onClick={() => !option.disabled && onPick(option.value)}
            onKeyDown={move}
            onPointerEnter={() => onPreview?.(option.value)}
            onPointerLeave={() => onPreview?.(null)}
            onFocus={() => onPreview?.(option.value)}
            onBlur={() => onPreview?.(null)}
          >
            {render(option.value, checked)}
          </button>
        );
      })}
    </div>
  );
}
